import { useEffect, useMemo, useState } from 'react';
import { Send } from 'lucide-react';
import { Sheet } from '@/shared/ui/Sheet';
import { Avatar } from '@/shared/ui/Avatar';
import { useStore } from '@/shared/state/store';
import { getUserById } from '@/shared/data';
import { formatRelativeTime } from '@/shared/utils/format';
import type { Comment } from '@/shared/types';

type Props = {
  reelId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function CommentsSheet({ reelId, open, onOpenChange }: Props) {
  const allComments = useStore((s) => s.comments);
  const comments = useMemo(
    () =>
      reelId ? allComments.filter((c) => c.target === 'reel' && c.postId === reelId) : [],
    [allComments, reelId],
  );

  // Browser-back closes the sheet first. Push a transient history entry on open
  // and intercept popstate to invoke onOpenChange(false).
  useEffect(() => {
    if (!open) return;
    window.history.pushState({ sheet: true }, '');
    const handler = () => onOpenChange(false);
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [open, onOpenChange]);

  const isOpen = open && !!reelId;

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange} side="bottom" heightClass="h-[60vh]">
      <div data-testid="comments-sheet" className="h-full flex flex-col">
        <div className="pt-3 pb-2 flex justify-center">
          <span className="w-10 h-1 rounded-full bg-white/20" />
        </div>
        <div className="px-4 pb-2 text-xs uppercase tracking-widest text-white/40">Comments</div>
        <div className="flex-1 overflow-y-auto pb-20">
          {reelId && <CommentList comments={comments} />}
        </div>
        {reelId && <CommentInput targetId={reelId} />}
      </div>
    </Sheet>
  );
}

function CommentList({ comments }: { comments: Comment[] }) {
  if (comments.length === 0) {
    return (
      <div className="text-white/40 text-sm italic px-4 py-6">
        No comments yet — be the first.
      </div>
    );
  }
  return (
    <ul className="px-4 space-y-4">
      {comments.map((c) => {
        const u = getUserById(c.authorId);
        const username = u?.username ?? 'guest';
        const avatar =
          u?.avatar ??
          'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=200&q=80';
        return (
          <li key={c.id} className="flex gap-3">
            <Avatar src={avatar} alt={username} size={32} />
            <div className="flex-1">
              <div className="text-sm">
                <span className="text-white/90 font-medium">@{username}</span>{' '}
                <span className="text-white/80">{c.text}</span>
              </div>
              <div className="text-[10px] text-white/40 mt-1 uppercase tracking-wider">
                {formatRelativeTime(c.createdAt)}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function CommentInput({ targetId }: { targetId: string }) {
  const [text, setText] = useState('');
  const addComment = useStore((s) => s.addComment);
  const submit = () => {
    const t = text.trim();
    if (!t) return;
    addComment({ target: 'reel', targetId, text: t });
    setText('');
  };
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      className="absolute bottom-4 left-0 right-0 px-4 z-30"
    >
      <div className="glass-strong rounded-full flex items-center gap-2 px-4 py-2.5">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a comment…"
          className="bg-transparent flex-1 outline-none text-sm text-white/90 placeholder:text-white/40"
        />
        <button
          type="submit"
          disabled={!text.trim()}
          aria-label="Post comment"
          className="text-rose-300 disabled:text-white/20 active:scale-90 transition-transform"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </form>
  );
}
